import {
  Opacity2,
  Opacity3,
  Opacity4,
  OpacityDark1,
  OpacityDark2,
  OpacityDark3,
} from '@trello/colors';
import type { GetDynamicTokens } from './getDynamicTokens';

/**
 * Assign dynamic background colors for buttons that are rendered directly atop
 * a board background based on backgroundBrightness.
 *
 * Historically, we fetched data about board backgrounds in a variety of
 * different components to determine backgroundBrightness prior to rendering,
 * and bifurcating Nachos button appearance between `transparent` and
 * `transparent-dark` to suit the backgroundBrightness.
 *
 * Now that we have access to CSS variables, we can rely wholly on these, and
 * remove all of this type of logic from render flows. Moving forward, these
 * values can be used directly, and we can deprecate:
 *
 * - Nachos: transparent vs transparent-dark
 */
export const getDynamicButtonTokens: GetDynamicTokens<'dynamic.button'> = ({
  background: { backgroundBrightness },
  theme,
}) => {
  if (theme === null) {
    if (backgroundBrightness === 'dark') {
      return {
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button': Opacity2,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button.hovered': Opacity3,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button.pressed': Opacity4,
      };
    } else {
      return {
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button': OpacityDark1,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button.hovered': OpacityDark2,
        // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- EXCEPTION (opacity)
        'dynamic.button.pressed': OpacityDark3,
      };
    }
  }

  if (backgroundBrightness === 'dark') {
    return {
      'dynamic.button': 'transparent',
      // Equivalent to --ds-background-neutral-hovered in ADS dark mode:
      'dynamic.button.hovered': '#A6C5E229',
      // Equivalent to --ds-background-neutral-pressed in ADS dark mode:
      'dynamic.button.pressed': '#BFDBF847',
    };
  } else {
    return {
      'dynamic.button': 'transparent',
      // Equivalent to --ds-background-neutral-hovered in ADS light mode:
      'dynamic.button.hovered': '#091E4224',
      // Equivalent to --ds-background-neutral-pressed in ADS light mode:
      'dynamic.button.pressed': '#091E424F',
    };
  }
};
